import {
  TileFolderIconContainer,
  TileFolderHeaderOverlay,
  TileCardContainer,
  TitleTitle,
  TileDescription,
  MetaDataContainer,
  MetaDataItemContainer,
  MetaDataText,
  TileTextContainer,
  RoleBagdeContainer
} from './styled-component'
import { Folder, Pins, Task } from '../stories/atoms/icons/entities'
import { LastActivity } from '../stories/atoms/icons/core'
import { iconSizeOptions } from '../stories/atoms/icons/icons-sizes'
import TileCardHeader, { TileCardHeaderButtons } from './tile-card-header'
import { projectsAPI } from '../../../proxyConfig/apiUrls'
import { useIntl } from 'react-intl'
import { useAccessSearchParam } from '../../zustand/authentication/use-auth-store'
import { handleRoleColor } from '../copy-project-modal/constants'
import {
  badgeSizeOptions,
  badgesVariantOptions
} from '../stories/atoms/badges/constants'
import BadgesComponent from '../stories/atoms/badges'
import { formatRelativeDate } from '../../utils/format-relative-date'
import SkeletonComponent from '../stories/atoms/skeleton'
import PropTypes from 'prop-types'

const defaultMetaData = {
  lastActivity: null
}

const MetaDataItem = ({ number, text, icon, disabled }) => {
  const isLoading = number < 0

  return (
    <MetaDataItemContainer disabled={disabled}>
      {icon}
      {isLoading ? (
        <SkeletonComponent
          isLoading
          skeletonShape={{
            width: '24px',
            height: '16px',
            borderRadius: '6px'
          }}
        />
      ) : (
        <MetaDataText disabled={disabled}>{text ?? number}</MetaDataText>
      )}
    </MetaDataItemContainer>
  )
}

const TileCardComponent = ({
  item,
  isFolder = false,
  metaData = defaultMetaData,
  contextMenu = null,
  disabled = false,

  // image URL for the tile header; defaults to the project logo endpoint,
  // callers rendering other entity types (e.g. plans) must pass their own
  image,

  hasCheckbox = false,
  isSelected = false,
  setIsSelected = () => {},

  // whether a folder tile also renders the checkbox/settings/context-menu
  // row (overlaid on the folder icon, same as it is on a plan's image)
  showFolderHeader = false,

  // caller-provided handler for the settings gear overlay; only shown when
  // both this is set and the item's role qualifies (see tile-card-header)
  onOpenSettings = null,
  ...props
}) => {
  const { formatMessage, locale } = useIntl()
  const accessToken = useAccessSearchParam()
  const hasMetaData = Object.values(metaData).some((value) => value !== null)
  const resolvedImage =
    image ?? `${projectsAPI}/${item.id}/logo${accessToken()}`

  const hasPinsAndTasksMetaData =
    metaData.openPins !== undefined || metaData.openTasks !== undefined

  const roleType = item.role?.type
  const roleName = item.role?.name

  const roleLabel = roleType
    ? formatMessage({
        id: `personas_roleLabel${roleType}`,
        defaultMessage: roleName ?? roleType
      })
    : (roleName ?? '')

  // handlers
  const getFolderText = (items) => {
    const key = items === 1 ? 'projects_items1' : 'projects_itemsOther'
    return `${items} ${formatMessage({ id: key })}`
  }

  const description = isFolder ? getFolderText(item.items) : item.companyName

  return (
    <TileCardContainer {...props} isSelected={isSelected}>
      {isFolder ? (
        <TileFolderIconContainer>
          <Folder style={{ width: '144px', height: '144px' }} />
          {showFolderHeader && (
            <TileFolderHeaderOverlay>
              <TileCardHeaderButtons
                item={item}
                contextMenu={contextMenu}
                disabled={disabled}
                hasCheckbox={hasCheckbox}
                isSelected={isSelected}
                setIsSelected={setIsSelected}
                onOpenSettings={onOpenSettings}
              />
            </TileFolderHeaderOverlay>
          )}
        </TileFolderIconContainer>
      ) : (
        <TileCardHeader
          item={item}
          image={resolvedImage}
          roleName={roleName}
          contextMenu={contextMenu}
          disabled={disabled}
          hasCheckbox={hasCheckbox}
          isSelected={isSelected}
          setIsSelected={setIsSelected}
          onOpenSettings={onOpenSettings}
        />
      )}
      <TileTextContainer>
        {roleLabel && (
          <RoleBagdeContainer>
            <BadgesComponent
              variant={badgesVariantOptions.STRONG}
              size={badgeSizeOptions.SMALL}
              color={handleRoleColor(roleType)}
              disabled={disabled}
            >
              {roleLabel}
            </BadgesComponent>
          </RoleBagdeContainer>
        )}
        <TitleTitle disabled={disabled}>{item.name}</TitleTitle>
        {description && (
          <TileDescription disabled={disabled}>{description}</TileDescription>
        )}
      </TileTextContainer>
      {hasMetaData && (
        <MetaDataContainer>
          {hasPinsAndTasksMetaData && (
            <>
              <MetaDataItem
                number={Number(metaData?.openPins)}
                icon={<Pins size={iconSizeOptions['ICON-S']} />}
                disabled={disabled}
              />
              {Number(metaData?.openTasks) >= 0 && (
                <MetaDataItem
                  number={Number(metaData?.openTasks)}
                  icon={<Task size={iconSizeOptions['ICON-S']} />}
                  disabled={disabled}
                />
              )}
            </>
          )}
          <MetaDataItem
            text={formatRelativeDate(
              metaData.lastActivity,
              locale,
              formatMessage
            )}
            icon={<LastActivity size={iconSizeOptions['ICON-S']} />}
            disabled={disabled}
          />
        </MetaDataContainer>
      )}
    </TileCardContainer>
  )
}

TileCardComponent.propTypes = {
  item: PropTypes.object.isRequired,
  isFolder: PropTypes.bool,
  metaData: PropTypes.object,
  contextMenu: PropTypes.node,
  disabled: PropTypes.bool,
  image: PropTypes.string,
  hasCheckbox: PropTypes.bool,
  isSelected: PropTypes.bool,
  setIsSelected: PropTypes.func,
  showFolderHeader: PropTypes.bool,
  onOpenSettings: PropTypes.func
}

export default TileCardComponent
